import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { User } from 'api/models';
import { CsatAPI, Template } from 'api/reports/csat';
import { ProjectInUserProfile } from 'api/user';
import { Filter } from 'components/molecules/filters/types';
import {
  transformCsatData,
  transformProjectManagersRatings,
  transformTemplateRatings,
} from 'components/organisms/csat/overview/utils';
import { FieldFilter } from 'components/organisms/tasks/filters';

const REPORTS_DATA_STALE_TIME = 360000;

const queryConfig = {
  staleTime: REPORTS_DATA_STALE_TIME,
};

const validateFilters = (criteria: FieldFilter[]) =>
  criteria.filter(FieldFilter.isValid);

export const useGetCSATData = (
  { criteria, type }: Filter,
  startDate: string,
  endDate: string,
  users: User[],
  templates: Template[],
  projects: ProjectInUserProfile[],
) => {
  const validFilters = validateFilters(criteria);
  const filter: Filter = useMemo(
    () => ({ criteria: validFilters, type }),
    [validFilters, type],
  );
  const getFilteredCsatData = useCallback(
    () =>
      CsatAPI.getCsatData({
        filter,
        endDate,
        startDate,
      })
        .then((response) =>
          transformCsatData(response, projects, users, templates),
        )
        .catch(() => {}),
    [endDate, filter, projects, startDate, templates, users],
  );

  return useQuery(
    ['csat-filter', { filter, startDate, endDate }],
    getFilteredCsatData,
  );
};

export const useOverAllRatingsQuery = (
  { criteria, type }: Filter,
  startDate: string,
  endDate: string,
) => {
  const validFilters = validateFilters(criteria);
  const filter: Filter = useMemo(
    () => ({ criteria: validFilters, type }),
    [validFilters, type],
  );
  const overallQueryFn = useCallback(
    () => CsatAPI.getOverallRating({ filter, endDate, startDate }),
    [endDate, filter, startDate],
  );

  return useQuery(
    ['csat-overall', { filter, startDate, endDate }],
    overallQueryFn,
    queryConfig,
  );
};

export const useAvgProjectManagerRatingsQuery = (
  { criteria, type }: Filter,
  startDate: string,
  endDate: string,
  users: User[],
) => {
  const validFilters = validateFilters(criteria);
  const filter: Filter = useMemo(
    () => ({ criteria: validFilters, type }),
    [validFilters, type],
  );
  const getAvgProjectMangerRatingQueryFn = useCallback(() => {
    return CsatAPI.getAvgProjectManagerRatings({
      filter,
      endDate,
      startDate,
    }).then((response) => transformProjectManagersRatings(response, users));
  }, [filter, endDate, startDate, users]);

  return useQuery(
    ['csat-get-avg-project-manager', { filter, startDate, endDate }],
    getAvgProjectMangerRatingQueryFn,
    queryConfig,
  );
};
export const useAvgTemplateRatingsQuery = (
  { criteria, type }: Filter,
  startDate: string,
  endDate: string,
  templates: Template[],
) => {
  const validFilters = validateFilters(criteria);
  const filter: Filter = useMemo(
    () => ({ criteria: validFilters, type }),
    [validFilters, type],
  );
  const getAvgTemplateRatingQueryFn = useCallback(
    () =>
      CsatAPI.getAvgTemplateRatings({ filter, endDate, startDate }).then(
        (response) => transformTemplateRatings(response, templates),
      ),
    [filter, endDate, startDate, templates],
  );

  return useQuery(
    ['csat-get-avg-template-ratings', { filter, startDate, endDate }],
    getAvgTemplateRatingQueryFn,
    queryConfig,
  );
};
